<script lang="ts" setup>
import ReportsHero from '@/domains/investor/components/Hero/Hero.vue'
import UiSection from '@/components/UiSection/UiSection.vue'
import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiDrawer from '@/components/UiDrawer/UiDrawer.vue'
import { useFetchFaq } from '@/domains/investor/composables/useFetchFaq'
import type { IDrawer } from '@/components/UiDrawer/UiDrawer.types'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import type { Document } from '@contentful/rich-text-types'
import type { LOCALE_CODE } from '~/types/contentful'
import type { TFaqItemMaybeRaw } from '@/domains/investor/faq/types/faq.types'
import { getLocalizedValue } from '@/domains/investor/utils/utils'

definePageMeta({
  layout: 'investor',
})

const { fetchFaq } = await useFetchFaq()
const faqResponse = await fetchFaq('dla-inwestora-daneprezentacje-faq')
const { t, locale } = useI18n()
const activeDrawersFaqValues = ref([0])

useHead(() => ({
  title: t('pageTitles.investorDataPresentationsFaq.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.investorDataPresentationsFaq.meta'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: t(`pageTitles.investorDataPresentationsFaq.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.investorDataPresentationsFaq.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.investorDataPresentationsFaq.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.investorDataPresentationsFaq.canonical.pl'),
    },
  ],
}))

const mapFaqItemToDrawer = (item: TFaqItemMaybeRaw): IDrawer => {
  const adaptedQuestion = item.question
  const adaptedAnswer = item.answer

  if (adaptedQuestion || adaptedAnswer) {
    return {
      header: adaptedQuestion || '',
      description: adaptedAnswer || '',
    }
  }

  const localizedQuestion = getLocalizedValue(item.fields?.faqItemQuestion, locale.value as LOCALE_CODE) as string | undefined
  const localizedAnswerDocument = getLocalizedValue(item.fields?.faqItemAnswer, locale.value as LOCALE_CODE) as Document | undefined

  return {
    header: localizedQuestion || '',
    description: localizedAnswerDocument ? documentToHtmlString(localizedAnswerDocument) : '',
  }
}

const resolveFaqItems = (items: unknown): TFaqItemMaybeRaw[] => {
  const localizedItems = getLocalizedValue(items, locale.value as LOCALE_CODE)
  return Array.isArray(localizedItems) ? localizedItems : []
}

const checkIsOpenDrawer = (index: number) => {
  return activeDrawersFaqValues.value.includes(index)
}

const faqValuesDrawer = computed<IDrawer[]>(() => {
  const items = resolveFaqItems(faqResponse.data.value?.items)
  return items.map(item => mapFaqItemToDrawer(item))
})

const toggleFaqValuesDrawer = (index: number) => {
  if (activeDrawersFaqValues.value.includes(index)) {
    activeDrawersFaqValues.value = activeDrawersFaqValues.value.filter(el => el !== index)
  } else {
    activeDrawersFaqValues.value.push(index)
  }
}

</script>

<template>
  <div>
    <ReportsHero
      :title="$t('investor.faq.hero.title')"
      :lead="$t('investor.faq.hero.lead')"
      image="/images/investor/hero/faq.png"
    />
    
    <UiSection
      id="faq"
      class="faq-section"
    >

      <UiHeader
        :header="$t('investor.faq.title')"
        position="center"
        :class="'faq-header'"
        header-size="h5"
        header-size-tablet="h5"
        header-size-mobile="h5"
      />

      <div class="section__container">
        <UiDrawer
          v-for="(drawer, index) in faqValuesDrawer"
          :key="drawer.header"
          :class="{ 'faq-drawer--open': checkIsOpenDrawer(index) }"
          :header="drawer.header"
          :description="drawer.description"
          :variant="'tertiary'"
          :icon="drawer.icon"
          :is-open="checkIsOpenDrawer(index)"
          @clicked="() => { toggleFaqValuesDrawer(index) }"
        />
      </div>
    </UiSection>
  </div>
</template>

<style lang="scss" scoped>

:deep(.single-drawer) {
  margin-bottom: 2.4rem;
  padding: 2.4rem;
  border: 1px solid $color-gray-blue-50;
}

:deep(.single-drawer__description-line) {
  display: none;
  background-color: transparent;
}

:deep(.single-drawer__description) {
  padding: 0;
  background-color: transparent;

  p {
    @include body-3;
    padding-bottom:1.6rem;
    color: $text-300;

    &:last-of-type {
      padding-bottom: 0;
    }
  }
}

:deep(.single-drawer__header-icon) {
  min-width: 2.4rem;
  width: 2.4rem;
  height: 2.4rem;
  background-image: url('/icons/arrow_down_purple.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: 0;
  transition: transform 0.3s ease;
}

:deep(.single-drawer__header-icon-line) {
  display: none;
}

:deep(.faq-drawer--open.single-drawer .single-drawer__header-icon) {
  transform: rotate(180deg);
}

@include atM {
  :deep(.investorHero__content) {
    max-width: 40rem;
  }
}

@include atL {
  :deep(.investorHero__content) {
    max-width: 49rem;
  }
}

</style>
